import axios from 'axios';

import { API } from '@app/constants';
import {
  APP_VERSION_TIMESTAMP,
  IS_APP_VERSION_REPORTED,
} from '@app/data/version';
import { billingStatus } from '@app/services/__global__/billingStatus';
import { integrationActionLog } from '@app/services/__global__/integrationActionLog';
import { serverStatus } from '@app/services/__global__/serverStatus';
import {
  getFrontendVersionHeaderValue,
  HEADER_FRONT_VERSION,
} from '@app/shared/utils';
import { getAccessToken } from '@app/utils/accessTokenStorage';
import { defineHostWithProtocol } from '@app/utils/defineHostWithProtocol/defineHostWithProtocol';

let isInterceptorInstalled = false;

// Значение статуса блокировки филиала приходит как поле `BillingStatus` в общем
// envelope ответа CRM (либо, как запасной вариант, одноимённым заголовком).
// Возвращаем `undefined`, если поле отсутствует, чтобы не сбрасывать текущий
// статус на ответах, которые его не несут (raw/строковые/book-host).
const extractBillingStatus = (data: unknown, headers: unknown): unknown => {
  if (data && typeof data === 'object' && 'BillingStatus' in data) {
    return (data as { BillingStatus?: unknown }).BillingStatus;
  }

  if (headers && typeof headers === 'object') {
    const headerBag = headers as Record<string, unknown>;

    return headerBag['billingstatus'] ?? headerBag['billing-status'];
  }

  return undefined;
};

const updateBillingStatus = (data: unknown, headers: unknown) => {
  const raw = extractBillingStatus(data, headers);
  if (raw === undefined) return;

  billingStatus.setStatus(raw);
};

export const setAxiosConfig = (isBookHost?: boolean) => {
  const { defaults } = axios;

  const accessToken = getAccessToken();

  const frontVersionHeaderValue = getFrontendVersionHeaderValue(
    APP_VERSION_TIMESTAMP
  );

  defaults.baseURL = defineHostWithProtocol({ isBookHost }) + API;
  defaults.headers.common['Content-Type'] = 'application/json';

  // Локальная сборка версию не сообщает (issue/11217, rspack.config.js).
  if (IS_APP_VERSION_REPORTED) {
    defaults.headers.common[HEADER_FRONT_VERSION] = frontVersionHeaderValue;
  }

  if (accessToken) {
    defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  } else {
    delete defaults.headers.common['Authorization'];
  }

  if (!isInterceptorInstalled) {
    axios.interceptors.request.use((config) => {
      if (IS_APP_VERSION_REPORTED) {
        config.headers.set(
          HEADER_FRONT_VERSION,
          getFrontendVersionHeaderValue(APP_VERSION_TIMESTAMP)
        );
      }

      // Журнал действий с интеграциями: пишет только пока открыт их реестр и
      // только запросы самих интеграций (us/11024).
      integrationActionLog.markRequest(config);

      return config;
    });

    axios.interceptors.response.use(
      (response) => {
        const contentType = response.headers?.['content-type'] || '';
        const isHtmlResponse =
          typeof contentType === 'string' && contentType.includes('text/html');
        const isJsonExpected =
          !response.config?.responseType ||
          response.config?.responseType === 'json';

        if (isHtmlResponse && isJsonExpected) {
          serverStatus.recordFailure();
        } else {
          serverStatus.recordSuccess();
        }

        updateBillingStatus(response.data, response.headers);
        integrationActionLog.recordResponse(response);

        return response;
      },
      (error) => {
        const status = error?.response?.status;
        const isNetworkError =
          !error?.response && error?.code === 'ERR_NETWORK';
        const isServerError =
          status === 502 || status === 503 || status === 504;

        if (isNetworkError) {
          serverStatus.recordCriticalFailure();
        } else if (isServerError) {
          serverStatus.recordFailure();
        } else {
          serverStatus.recordSuccess();
        }

        if (error?.response) {
          updateBillingStatus(error.response.data, error.response.headers);
        }

        integrationActionLog.recordFailure(error?.config, error);

        return Promise.reject(error);
      }
    );

    isInterceptorInstalled = true;
  }
};
